import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/lib/auth";
import { usePageMeta } from "@/lib/format";
import { Spinner } from "@/components/Spinner";
import { toast } from "sonner";
import { ChevronLeft, ArrowUp, Trash2 } from "lucide-react";

export default function DirectMessage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useI18n();
  const [msgs, setMsgs] = useState(null);
  const [other, setOther] = useState(location.state || null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  usePageMeta(other?.name || t("message"));

  const load = useCallback(async () => {
    if (!userId) return;
    const [m, coach] = await Promise.all([
      api.get(`/messages/${userId}`).then((r) => r.data).catch(() => []),
      api.get(`/coaches/${userId}`).then((r) => r.data).catch(() => null),
    ]);
    setMsgs(Array.isArray(m) ? m : []);
    if (coach) setOther({ name: coach.name, photo: coach.profile?.photo });
    api.post(`/messages/${userId}/seen`).catch(() => {});
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs]);

  const send = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    setText("");
    try { await api.post("/messages", { to_user_id: userId, body }); await load(); }
    catch (e) { toast.error(apiError(e)); setText(body); }
    finally { setSending(false); }
  };

  const remove = async (m) => {
    if (m.deleted) return;
    setMsgs((prev) => prev.map((x) => (x.id === m.id ? { ...x, deleted: true, body: "" } : x)));
    try { await api.delete(`/messages/${m.id}`); }
    catch (e) { toast.error(apiError(e)); await load(); }
  };

  return (
    <div className="mx-auto flex h-[calc(100vh-11rem)] max-w-2xl flex-col overflow-hidden rounded-2xl border border-border bg-card" data-testid="dm-thread-screen">
      <div className="flex items-center gap-3 border-b border-border p-3">
        <button onClick={() => navigate("/app/messages")} data-testid="dm-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        {other?.photo ? <img src={other.photo} alt="" className="h-9 w-9 rounded-full object-cover" /> : <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15 text-sm font-semibold text-primary">{(other?.name || "·")[0]}</span>}
        <p className="min-w-0 flex-1 truncate font-display text-base text-foreground">{other?.name || t("message")}</p>
      </div>

      <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto p-4" data-testid="dm-messages">
        {msgs === null ? <Spinner label={t("common.loading")} /> : msgs.length === 0 ? (
          <p className="py-12 text-center text-sm italic text-muted-foreground">{t("no_messages_hint")}</p>
        ) : msgs.map((m) => {
          const mine = m.from_user_id === user?.id;
          return (
            <div key={m.id} className={`group flex items-center gap-2 ${mine ? "justify-end" : "justify-start"}`} data-testid={`dm-bubble-${m.id}`}>
              {mine && !m.deleted ? (
                <button onClick={() => remove(m)} data-testid={`dm-delete-${m.id}`} className="opacity-0 transition-opacity group-hover:opacity-100"><Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-rose-600" /></button>
              ) : null}
              <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${m.deleted ? "bg-secondary/50 italic text-muted-foreground" : mine ? "bg-primary text-primary-foreground" : "bg-secondary/60 text-foreground"}`}>
                {m.deleted ? t("message_deleted") : m.body}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-end gap-2 border-t border-border p-3">
        <textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder={t("write_a_message")} rows={1} data-testid="dm-input" className="max-h-28 min-h-[44px] flex-1 resize-none rounded-2xl border border-border bg-background px-3.5 py-2.5 text-sm outline-none focus:border-primary" />
        <button onClick={send} disabled={!text.trim() || sending} data-testid="dm-send" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground disabled:opacity-50"><ArrowUp className="h-5 w-5" /></button>
      </div>
    </div>
  );
}
